import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Flame, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { brandOf, textOn, visibleOnDark } from "@/components/fusion/FusionShared";

/** /fusion/join/:code — athletes create their Fusion fundraising account via the coach's link. */
export default function FusionJoinPage() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", position: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get(`/fusion/public/join/${code}`).then((r) => setInfo(r.data)).catch(() => setErr(true));
  }, [code]);

  if (err) return <Shell><div className="text-white/60 text-center pt-24" data-testid="join-invalid">This invite link is invalid or the event has closed. Ask your coach for a new link.</div></Shell>;
  if (!info) return <Shell><div className="text-white/40 text-center pt-24 animate-pulse">Loading…</div></Shell>;

  const e = info.event;
  const b = brandOf(e);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!f.name.trim()) return toast.error("Enter your full name");
    if (!/^[\w.\-+]+@[\w.\-]+\.[a-zA-Z]{2,}$/.test(f.email)) return toast.error("A valid email is required");
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(`/fusion/public/join/${code}`, { name: f.name.trim(), email: f.email.trim(), password: f.password, position: f.position.trim() || null });
      toast.success("Account created — welcome to the Hit-A-Thon!");
      setTimeout(() => { window.location.href = "/portal/fusion"; }, 900);
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };

  const cls = "bg-white/5 border-white/15 text-white placeholder:text-white/35";
  return (
    <Shell primary={b.primary}>
      <div className="max-w-md mx-auto px-5 pb-20">
        <header className="pt-12 text-center">
          {b.logo && <img src={b.logo} alt={b.school} className="h-16 w-16 mx-auto object-contain rounded-xl bg-white/90 p-1.5" />}
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] font-bold flex items-center justify-center gap-2" style={{ color: b.secondary }}>
            <Flame className="h-3.5 w-3.5" /> BioHacker Fusion™
          </div>
          <h1 className="text-3xl font-black text-white mt-2" data-testid="join-title">Join the {e.name}</h1>
          <p className="text-sm text-white/55 mt-2">
            {b.school} is raising money through a performance combine + Hit-A-Thon.
            Create your athlete account to get your personal fundraising page.
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3" data-testid="join-form">
          <Input placeholder="Full name *" value={f.name} onChange={set("name")} className={cls} data-testid="join-name-input" />
          <Input type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={cls} data-testid="join-email-input" />
          <Input type="password" placeholder="Create a password (8+ characters) *" value={f.password} onChange={set("password")} className={cls} data-testid="join-password-input" />
          <Input placeholder="Position (optional)" value={f.position} onChange={set("position")} className={cls} data-testid="join-position-input" />
          <Button type="submit" disabled={busy} className="w-full font-black py-6" style={{ background: visibleOnDark(b.secondary), color: textOn(visibleOnDark(b.secondary)) }} data-testid="join-submit-btn">
            {busy ? "Creating your account…" : "Create my fundraising account"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="text-[11px] text-white/40 text-center">
            You'll get your own campaign page, share tools, and an optional AI fundraising video.
            Already have an account? <a href="/fusion/login" className="underline text-white/60">Sign in to Fusion</a>
            <span className="mx-1.5">·</span>
            <a href="/player-guide" target="_blank" rel="noreferrer" className="underline text-white/60" data-testid="join-guide-link">Read the Player Guide</a>
          </div>
        </form>

        <div className="mt-8 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
          Raise. Measure. Develop. Repeat.™
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, primary = "#0B1E36" }) {
  return (
    <div className="min-h-screen" style={{ background: `radial-gradient(1200px 500px at 50% -100px, ${primary}66, transparent), #080e1c` }}>
      {children}
    </div>
  );
}
